import { Dialog, DialogClose, DialogTopRight } from '@flashka/shared/ui';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { postAuthPath } from '../post-auth-path';
import { useAuth } from '../session';
import { authDialogs, useAuthDialogs } from '../store';
import { AccountDialog } from './account-dialog';
import { AuthCard } from './auth-card';

/**
 * Single mount point (inside AuthProvider) for the modal presentations:
 * sign-in/sign-up behind SignInButton/SignUpButton, and the account-management
 * dialog behind `useAuthControls().openUserProfile()`.
 */
export function AuthDialogs() {
  const { t } = useTranslation();
  const { open } = useAuthDialogs();
  const authOpen = open === 'sign-in' || open === 'sign-up';
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Landing pages hand off to the dashboard once signed in; everywhere else
  // the dialog just closes over the page the user was already on.
  const destination = postAuthPath(pathname);
  const { isSignedIn } = useAuth();

  // Signed in → leave only once the SESSION has arrived (one round trip after
  // the sign-in call). Leaving at once landed on a protected page that still
  // saw no session and drew a second sign-in card for a beat. The card keeps
  // its spinner meanwhile; a session that never shows lets go after 8 s.
  const [awaitingSession, setAwaitingSession] = useState<string | null | false>(false);
  useEffect(() => {
    if (awaitingSession === false) return;
    const leave = () => {
      setAwaitingSession(false);
      authDialogs.close();
      if (awaitingSession) navigate(awaitingSession);
    };
    if (isSignedIn) {
      leave();
      return;
    }
    const timer = setTimeout(leave, 8_000);
    return () => clearTimeout(timer);
  }, [awaitingSession, isSignedIn, navigate]);

  return (
    <>
      {/* Composed DS Dialog: AuthCard renders its own visible heading (and
          `plain`, so the panel isn't clipping a second card), so the panel is
          labelled through `accessibilityLabel` instead of a DS title row. */}
      <Dialog
        open={authOpen}
        onClose={() => authDialogs.close()}
        size="md"
        hug
        accessibilityLabel={t('auth.continue.title')}>
        <DialogTopRight>
          <DialogClose variant="secondary" accessibilityLabel={t('a11y.close')} />
        </DialogTopRight>
        {/* One unified card for both entry points: SignInButton and
            SignUpButton still exist across the app, but sign-in vs sign-up
            is decided by the flow itself (shared `emailContinue`), not by
            which button opened the dialog. */}
        {authOpen && (
          <AuthCard
            variant="plain"
            // OAuth never comes back through onAuthenticated — the worker's
            // redirect decides — so the destination goes into the callback too.
            callbackPath={destination ?? undefined}
            onAuthenticated={() => setAwaitingSession(destination ?? null)}
          />
        )}
      </Dialog>
      <AccountDialog
        open={open === 'profile'}
        onOpenChange={(o) => !o && authDialogs.close()}
      />
    </>
  );
}
